import React, { useMemo, useState } from "react";
import Checkbox from "@material-ui/core/Checkbox";
import CircularProgress from "@material-ui/core/CircularProgress";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Typography from "@material-ui/core/Typography";
import { Link as RouterLink, useHistory } from "react-router-dom";

import Button from "components/CustomButtons/Button.js";
import CustomInput from "components/CustomInput/CustomInput.js";
import GlobalMessage from "components/GlobalMessage";
import { APP_STRINGS } from "constants/appStrings";

import "./index.css";

const isEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

export default function LoginPage() {
  const history = useHistory();
  const initialForm = useMemo(
    () => ({
      email: "",
      password: "",
      remember: true,
    }),
    []
  );

  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [feedback, setFeedback] = useState({ message: "", severity: "" });

  const setField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const handleChange = (event) => {
    const { id, name, value, checked, type } = event.target;
    const key = id || name;
    setField(key, type === "checkbox" ? checked : value);
  };

  const validate = () => {
    const nextErrors = {};

    if (!isEmail(form.email)) {
      nextErrors.email = "Informe um e-mail valido.";
    }

    if (!form.password || form.password.trim().length < 6) {
      nextErrors.password = "Informe uma senha com pelo menos 6 caracteres.";
    }

    return nextErrors;
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    const nextErrors = validate();
    setErrors(nextErrors);

    if (Object.keys(nextErrors).length) {
      setFeedback({
        message: "Revise os campos para continuar.",
        severity: "error",
      });
      setSnackbarOpen(true);
      return;
    }

    setLoading(true);

    window.setTimeout(() => {
      setLoading(false);
      setFeedback({
        message:
          "Acesso demonstrativo liberado. Redirecionando para o portal da cliente.",
        severity: "success",
      });
      setSnackbarOpen(true);
      history.push("/portal-cliente");
    }, 900);
  };

  return (
    <div className="login-page">
      <div className="login-page__background" />
      <div className="login-page__glow login-page__glow--top" />
      <div className="login-page__glow login-page__glow--bottom" />

      <main className="login-page__shell">
        <section className="login-page__hero">
          <span className="login-page__eyebrow">Area do cliente</span>
          <Typography component="h1" className="login-page__title">
            Um acesso reservado para uma experiencia juridica mais organizada.
          </Typography>
          <Typography component="p" className="login-page__text">
            Esta area foi pensada para concentrar relacionamento, acompanhamento
            e proximos passos com mais discricao, clareza e presenca
            profissional.
          </Typography>
        </section>

        <section className="login-page__panel">
          <div className="login-page__panel-header">
            <span className="login-page__brand">
              {APP_STRINGS.brand.desktop}
            </span>
            <RouterLink className="login-page__back" to="/">
              Voltar ao site
            </RouterLink>
          </div>

          <div className="login-page__card">
            <span className="login-page__card-tag">Login</span>
            <Typography component="h2" className="login-page__card-title">
              Entre na sua area exclusiva
            </Typography>
            <Typography component="p" className="login-page__card-text">
              Use seus dados de acesso para acompanhar documentos, comunicacoes
              e proximos passos do atendimento.
            </Typography>

            <form className="login-page__form" onSubmit={handleSubmit} noValidate>
              <div className="login-page__field">
                <CustomInput
                  labelText="E-mail"
                  id="email"
                  value={form.email}
                  onChange={handleChange}
                  formControlProps={{ fullWidth: true }}
                  inputProps={{
                    type: "email",
                    autoComplete: "email",
                    "aria-invalid": !!errors.email,
                    "aria-describedby": errors.email ? "login-email-error" : undefined,
                  }}
                />
                <div
                  id="login-email-error"
                  className={`login-page__helper${
                    errors.email ? " login-page__helper--error" : ""
                  }`}
                >
                  {errors.email || "Digite o e-mail cadastrado no atendimento."}
                </div>
              </div>

              <div className="login-page__field">
                <CustomInput
                  labelText="Senha"
                  id="password"
                  value={form.password}
                  onChange={handleChange}
                  formControlProps={{ fullWidth: true }}
                  inputProps={{
                    type: "password",
                    autoComplete: "current-password",
                    "aria-invalid": !!errors.password,
                    "aria-describedby": errors.password
                      ? "login-password-error"
                      : undefined,
                  }}
                />
                <div
                  id="login-password-error"
                  className={`login-page__helper${
                    errors.password ? " login-page__helper--error" : ""
                  }`}
                >
                  {errors.password || "Senha de acesso da area do cliente."}
                </div>
              </div>

              <div className="login-page__row">
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={form.remember}
                      onChange={handleChange}
                      color="primary"
                      id="remember"
                      name="remember"
                    />
                  }
                  label="Manter conectado neste dispositivo"
                />

                <a
                  className="login-page__link"
                  href={APP_STRINGS.links.whatsapp}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Preciso de ajuda
                </a>
              </div>

              <div className="login-page__actions">
                <Button
                  type="submit"
                  className="login-page__primary"
                  disabled={loading}
                >
                  {loading ? <CircularProgress size={20} /> : "Entrar"}
                </Button>

                <RouterLink className="login-page__secondary" to="/cadastro">
                  Criar cadastro
                </RouterLink>
              </div>
            </form>

            <div className="login-page__note">
              Ainda nao possui acesso? Comece pelo cadastro inicial para pessoa
              fisica ou empresa.
            </div>

            <RouterLink className="login-page__demo-link" to="/portal-cliente">
              Ver portal demonstrativo com dados mockados
            </RouterLink>
          </div>
        </section>
      </main>

      <GlobalMessage
        onClick={() => setSnackbarOpen(false)}
        snackbarOpen={snackbarOpen}
        message={feedback}
      />
    </div>
  );
}
